import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Empty,
  Input,
  Modal,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  getApiErrors,
  clearApiErrors,
  downloadApiErrorsCsv,
} from "../../utils/ErrorLog";
import {
  getFailedOrders,
  clearFailedOrders,
  removeFailedOrder,
  restoreFailedOrderToCart,
  downloadFailedOrdersCsv,
} from "../../utils/FailedOrders";

// Must match selectedProductNameLocalStorage in CustomerDashboard. Kept as a
// literal so the diagnostics page does not pull the whole dashboard in.
const CART_STORAGE_KEY = "selcted-Products";
const KIND_COLOURS = {
  network: "red",
  http: "volcano",
  timeout: "orange",
  blocked: "purple",
  canceled: "default",
  validation: "gold",
  unknown: "geekblue",
};

const KIND_HELP = {
  network:
    "No HTTP response reached the browser — offline, request aborted mid-flight (e.g. a page reload), or a response the browser blocked for CORS. This is what surfaces to users as 'Network Error'.",
  http: "The server replied with an error status code.",
  timeout: "The request was given up on before the server replied.",
  blocked:
    "The call never left the app because a token refresh was in progress.",
  canceled: "The request was cancelled deliberately (usually a page change).",
  validation: "Stopped before sending because the order itself was not valid.",
  unknown: "Could not be classified.",
};

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
}

function KindTag({ kind }) {
  return (
    <Tooltip title={KIND_HELP[kind] || ""}>
      <Tag color={KIND_COLOURS[kind] || "default"}>{kind}</Tag>
    </Tooltip>
  );
}

/* ------------------------------------------------------------------ */
/* Tab 1 — every failed API call                                       */
/* ------------------------------------------------------------------ */

function ApiErrorsTab() {
  const [entries, setEntries] = useState(() => getApiErrors());
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const refresh = useCallback(() => setEntries(getApiErrors()), []);

  useEffect(() => {
    window.addEventListener("api-error-logged", refresh);
    window.addEventListener("storage", refresh);
    const poll = setInterval(refresh, 5000);
    return () => {
      window.removeEventListener("api-error-logged", refresh);
      window.removeEventListener("storage", refresh);
      clearInterval(poll);
    };
  }, [refresh]);

  const kinds = useMemo(
    () => Array.from(new Set(entries.map((e) => e.kind))),
    [entries]
  );

  const counts = useMemo(() => {
    const out = {};
    entries.forEach((e) => {
      out[e.kind] = (out[e.kind] || 0) + 1;
    });
    return out;
  }, [entries]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (kindFilter !== "all" && e.kind !== kindFilter) return false;
      if (!needle) return true;
      return [
        e.url,
        e.message,
        e.label,
        e.status,
        e.userCode,
        e.page,
        e.requestData,
        e.responseData,
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [entries, search, kindFilter]);

  const columns = [
    {
      title: "When",
      dataIndex: "time",
      width: 170,
      render: (t) => formatTime(t),
      sorter: (a, b) => (a.time < b.time ? -1 : 1),
      defaultSortOrder: "descend",
    },
    {
      title: "Cause",
      dataIndex: "kind",
      width: 110,
      render: (kind) => <KindTag kind={kind} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 80,
      render: (s) => (s ? s : <span style={{ color: "#999" }}>—</span>),
    },
    {
      title: "Call",
      dataIndex: "url",
      render: (url, row) => (
        <div style={{ wordBreak: "break-all" }}>
          <b>{row.method}</b> {url}
          {row.label ? (
            <Tag style={{ marginLeft: 6 }} color="blue">
              {row.label}
            </Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      width: 220,
      render: (m) => <span style={{ wordBreak: "break-word" }}>{m}</span>,
    },
    {
      title: "User",
      dataIndex: "userCode",
      width: 110,
      render: (code, row) => (
        <span>
          {code || "—"}
          {row.appType ? (
            <div style={{ fontSize: 11, color: "#888" }}>{row.appType}</div>
          ) : null}
        </span>
      ),
    },
    {
      title: "Page",
      dataIndex: "page",
      width: 150,
      render: (p) => (
        <span style={{ fontSize: 12, wordBreak: "break-all" }}>{p}</span>
      ),
    },
    {
      title: "",
      width: 80,
      render: (_, row) => (
        <Button size="small" onClick={() => setDetail(row)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <>
      <p style={{ color: "#666", marginBottom: 16 }}>
        Every API call that failed on this device, newest first. Last 300 kept.
      </p>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Input.Search
          allowClear
          placeholder="Search url, message, payload, user…"
          style={{ maxWidth: 340 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={kindFilter}
          style={{ width: 190 }}
          onChange={setKindFilter}
          options={[
            { value: "all", label: "All causes (" + entries.length + ")" },
          ].concat(
            kinds.map((k) => ({
              value: k,
              label: k + " (" + (counts[k] || 0) + ")",
            }))
          )}
        />
        <Button onClick={refresh}>Refresh</Button>
        <Button
          type="primary"
          disabled={entries.length === 0}
          onClick={downloadApiErrorsCsv}
        >
          Export CSV
        </Button>
        <Button
          danger
          disabled={entries.length === 0}
          onClick={() =>
            Modal.confirm({
              title: "Clear the API error log?",
              content:
                "All " +
                entries.length +
                " recorded errors will be deleted from this device. Export first if you need them.",
              okText: "Clear",
              okButtonProps: { danger: true },
              onOk: () => {
                clearApiErrors();
                refresh();
              },
            })
          }
        >
          Clear
        </Button>
      </div>

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1100 }}
        locale={{
          emptyText: (
            <Empty
              description={
                entries.length === 0
                  ? "No API errors recorded yet."
                  : "No errors match this filter."
              }
            />
          ),
        }}
      />

      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={[
          <Button key="close" onClick={() => setDetail(null)}>
            Close
          </Button>,
        ]}
        width={860}
        title="Error detail"
      >
        {detail && (
          <div>
            <table style={{ width: "100%", marginBottom: 16, fontSize: 13 }}>
              <tbody>
                {[
                  ["Time", formatTime(detail.time)],
                  [
                    "Cause",
                    detail.kind + " — " + (KIND_HELP[detail.kind] || ""),
                  ],
                  ["Method", detail.method],
                  ["URL", detail.url],
                  ["Label", detail.label || "—"],
                  [
                    "Status",
                    detail.status
                      ? detail.status + " " + detail.statusText
                      : "no response",
                  ],
                  ["Message", detail.message],
                  ["Axios code", detail.code || "—"],
                  ["Page", detail.page],
                  [
                    "Browser online at the time",
                    detail.online === null ? "unknown" : String(detail.online),
                  ],
                  [
                    "User",
                    (detail.userCode || "—") +
                      (detail.userName ? " (" + detail.userName + ")" : "") +
                      (detail.appType ? " · " + detail.appType : ""),
                  ],
                  ["User agent", detail.userAgent],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <td
                      style={{
                        fontWeight: 600,
                        verticalAlign: "top",
                        width: 200,
                        padding: "4px 8px 4px 0",
                      }}
                    >
                      {k}
                    </td>
                    <td style={{ wordBreak: "break-word", padding: "4px 0" }}>
                      {v}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h5>Request payload</h5>
            <pre
              style={{
                background: "#f6f6f6",
                padding: 12,
                maxHeight: 260,
                overflow: "auto",
                fontSize: 12,
              }}
            >
              {detail.requestData || "(none)"}
            </pre>

            <h5>Response body</h5>
            <pre
              style={{
                background: "#f6f6f6",
                padding: 12,
                maxHeight: 260,
                overflow: "auto",
                fontSize: 12,
              }}
            >
              {detail.responseData ||
                "(no response body — the request never got one)"}
            </pre>
          </div>
        )}
      </Modal>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Tab 2 — orders that failed to submit, with their carts               */
/* ------------------------------------------------------------------ */

function FailedOrdersTab() {
  const [entries, setEntries] = useState(() => getFailedOrders());
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const refresh = useCallback(() => setEntries(getFailedOrders()), []);

  useEffect(() => {
    window.addEventListener("order-failure-logged", refresh);
    window.addEventListener("storage", refresh);
    const poll = setInterval(refresh, 5000);
    return () => {
      window.removeEventListener("order-failure-logged", refresh);
      window.removeEventListener("storage", refresh);
      clearInterval(poll);
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((e) =>
      [e.reason, e.message, e.custCode, e.custName, e.uniqueOrderID, e.remarks]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [entries, search]);

  function restore(entry) {
    Modal.confirm({
      title: "Put these " + entry.itemCount + " item(s) back in the cart?",
      content:
        "This replaces whatever is currently in the customer cart on this device with the items from this failed attempt.",
      okText: "Restore",
      onOk: () => {
        const n = restoreFailedOrderToCart(entry, CART_STORAGE_KEY);
        refresh();
        if (n > 0) {
          message.success(
            n + " item(s) restored. Open the customer dashboard to submit."
          );
        } else {
          message.error("Nothing to restore.");
        }
      },
    });
  }

  const columns = [
    {
      title: "When",
      dataIndex: "time",
      width: 170,
      render: (t) => formatTime(t),
      sorter: (a, b) => (a.time < b.time ? -1 : 1),
      defaultSortOrder: "descend",
    },
    {
      title: "Cause",
      dataIndex: "kind",
      width: 110,
      render: (kind) => <KindTag kind={kind} />,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (r) => <span style={{ wordBreak: "break-word" }}>{r}</span>,
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      width: 90,
      render: (n, row) => (
        <span>
          {n}
          <div style={{ fontSize: 11, color: "#888" }}>qty {row.totalQty}</div>
        </span>
      ),
    },
    {
      title: "Approx value",
      dataIndex: "approxValue",
      width: 110,
      render: (v) => (v ? "Rs. " + v : "—"),
    },
    {
      title: "Customer",
      dataIndex: "custCode",
      width: 120,
      render: (code, row) => (
        <span>
          {code || "—"}
          {row.custName ? (
            <div style={{ fontSize: 11, color: "#888" }}>{row.custName}</div>
          ) : null}
        </span>
      ),
    },
    {
      title: "State",
      dataIndex: "recovered",
      width: 100,
      render: (r) =>
        r ? <Tag color="green">restored</Tag> : <Tag>not restored</Tag>,
    },
    {
      title: "",
      width: 190,
      render: (_, row) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="small" onClick={() => setDetail(row)}>
            Items
          </Button>
          <Button
            size="small"
            type="primary"
            disabled={row.itemCount === 0}
            onClick={() => restore(row)}
          >
            Restore
          </Button>
          <Button
            size="small"
            danger
            onClick={() => {
              removeFailedOrder(row.id);
              refresh();
            }}
          >
            ✕
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <p style={{ color: "#666", marginBottom: 16 }}>
        Order submissions that did not go through, with the cart as it stood at
        the time. Nothing here was saved on the server. "Restore" puts the items
        back into the customer cart on this device so the order can be placed
        again. Last 50 kept.
      </p>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Input.Search
          allowClear
          placeholder="Search reason, customer, order id…"
          style={{ maxWidth: 340 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button onClick={refresh}>Refresh</Button>
        <Button
          type="primary"
          disabled={entries.length === 0}
          onClick={downloadFailedOrdersCsv}
        >
          Export CSV
        </Button>
        <Button
          danger
          disabled={entries.length === 0}
          onClick={() =>
            Modal.confirm({
              title: "Clear the failed-order record?",
              content:
                "All " +
                entries.length +
                " failed attempts (and their saved items) will be deleted. Export first if you need them.",
              okText: "Clear",
              okButtonProps: { danger: true },
              onOk: () => {
                clearFailedOrders();
                refresh();
              },
            })
          }
        >
          Clear
        </Button>
      </div>

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1100 }}
        locale={{
          emptyText: (
            <Empty
              description={
                entries.length === 0
                  ? "No failed order attempts recorded."
                  : "Nothing matches this search."
              }
            />
          ),
        }}
      />

      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        width={860}
        title="Failed order detail"
        footer={[
          <Button
            key="restore"
            type="primary"
            disabled={!detail || detail.itemCount === 0}
            onClick={() => {
              const entry = detail;
              setDetail(null);
              restore(entry);
            }}
          >
            Restore to cart
          </Button>,
          <Button key="close" onClick={() => setDetail(null)}>
            Close
          </Button>,
        ]}
      >
        {detail && (
          <div>
            <table style={{ width: "100%", marginBottom: 16, fontSize: 13 }}>
              <tbody>
                {[
                  ["Time", formatTime(detail.time)],
                  ["Reason", detail.reason],
                  ["Cause", detail.kind],
                  ["Raw message", detail.message || "—"],
                  ["HTTP status", detail.status || "no response"],
                  ["Order id used", detail.uniqueOrderID || "—"],
                  ["Remarks", detail.remarks || "—"],
                  [
                    "Approx value",
                    detail.approxValue ? "Rs. " + detail.approxValue : "—",
                  ],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <td
                      style={{
                        fontWeight: 600,
                        verticalAlign: "top",
                        width: 160,
                        padding: "4px 8px 4px 0",
                      }}
                    >
                      {k}
                    </td>
                    <td style={{ wordBreak: "break-word", padding: "4px 0" }}>
                      {v}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h5>Items in this attempt</h5>
            <Table
              rowKey={(r, i) => (r.productCode || "") + "-" + i}
              size="small"
              pagination={false}
              dataSource={detail.items || []}
              columns={[
                {
                  title: "Product",
                  render: (_, r) => r.prd1 || r.prodName || "—",
                },
                { title: "Code", dataIndex: "productCode", width: 110 },
                {
                  title: "Pack",
                  width: 80,
                  render: (_, r) => r.cs1 || r.case1 || "—",
                },
                {
                  title: "MRP",
                  width: 80,
                  render: (_, r) => r.mrp1 || r.mrp || "—",
                },
                {
                  title: "Rate",
                  width: 80,
                  render: (_, r) => r.rte || r.rate || "—",
                },
                { title: "Qty", dataIndex: "qty", width: 70 },
              ]}
            />
          </div>
        )}
      </Modal>
    </>
  );
}

/* ------------------------------------------------------------------ */

const ErrorLog = () => {
  const [failedCount, setFailedCount] = useState(
    () => getFailedOrders().length
  );

  useEffect(() => {
    const update = () => setFailedCount(getFailedOrders().length);
    window.addEventListener("order-failure-logged", update);
    const poll = setInterval(update, 5000);
    return () => {
      window.removeEventListener("order-failure-logged", update);
      clearInterval(poll);
    };
  }, []);

  return (
    <div style={{ padding: "24px 16px", maxWidth: 1400, margin: "0 auto" }}>
      <h3 style={{ marginBottom: 4 }}>Diagnostics</h3>
      <p style={{ color: "#666" }}>
        Kept in this browser's storage only — clearing site data wipes it, so
        export before you clear.
      </p>
      <Tabs
        defaultActiveKey={failedCount > 0 ? "orders" : "api"}
        items={[
          {
            key: "orders",
            label:
              "Failed orders" + (failedCount ? " (" + failedCount + ")" : ""),
            children: <FailedOrdersTab />,
          },
          { key: "api", label: "API errors", children: <ApiErrorsTab /> },
        ]}
      />
    </div>
  );
};

export default ErrorLog;
